import { useEffect, useState } from 'react';
import type { RoutingStatusResponse } from '@lousada/shared';
import { fetchRoutingStatus } from './api/endpoints.js';
import { fetchReadiness } from './health-client';
import { nowInLisbon } from './planner/datetime.js';
import { freshnessLine, validityLine } from './planner/freshness.js';
import { SiteFooter } from './planner/PlannerFooter.js';
import { buildStatusRows, summarise, type ReadinessOutcome, type StatusRow } from './status';
import { Topbar } from './ui/Topbar.js';

export interface AppProps {
  onNavigate?: (path: string) => void;
}

function BoardRow({ row }: { row: StatusRow }): React.JSX.Element {
  return (
    <li className="board__row">
      <span className="board__name">{row.label}</span>
      <span className={`board__state board__state--${row.tone}`}>
        <span aria-hidden="true" className="board__lamp" />
        {row.value}
      </span>
      {row.detail !== undefined && <p className="board__detail">{row.detail}</p>}
    </li>
  );
}

/**
 * The service page, read like a station information board: one lamp for the whole service,
 * then a line per part of it.
 */
export default function App(props: AppProps): React.JSX.Element {
  const [outcome, setOutcome] = useState<ReadinessOutcome>({ kind: 'loading' });
  const [routing, setRouting] = useState<RoutingStatusResponse | null>(null);
  const [checking, setChecking] = useState(true);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    void fetchReadiness(controller.signal).then((next) => {
      if (controller.signal.aborted) return;
      setOutcome(next);
      setChecking(false);
      setCheckedAt(nowInLisbon().time);
    });
    fetchRoutingStatus(controller.signal)
      .then(setRouting)
      .catch(() => undefined);
    return () => controller.abort();
  }, [attempt]);

  function recheck(): void {
    setChecking(true);
    setOutcome({ kind: 'loading' });
    setAttempt((previous) => previous + 1);
  }

  const rows = buildStatusRows(outcome);
  const summary = summarise(outcome);
  const freshness = freshnessLine(routing);
  const validity = validityLine(routing);

  return (
    <div className="status-page">
      <Topbar page="status" onNavigate={props.onNavigate} />

      <main className="status">
        <h1 className="status__title">Estado do serviço</h1>

        <section className="board" aria-labelledby="board-summary">
          <div className={`board__head board__state--${summary.tone}`}>
            <span aria-hidden="true" className="board__lamp board__lamp--large" />
            <p id="board-summary" aria-live="polite" className="board__summary">
              {summary.text}
            </p>
            <div className="board__check">
              {checkedAt !== null && !checking && (
                <span className="board__time">Verificado às {checkedAt}</span>
              )}
              <button
                className="board__recheck"
                type="button"
                onClick={recheck}
                disabled={checking}
              >
                {checking ? 'A verificar…' : 'Verificar de novo'}
              </button>
            </div>
          </div>

          <ul className="board__rows">
            {rows.map((row) => (
              <BoardRow key={row.id} row={row} />
            ))}
            {freshness !== null && (
              <li className="board__row">
                <span className="board__name">Horários</span>
                <span className="board__state board__state--online">
                  <span aria-hidden="true" className="board__lamp" />
                  {freshness.replace('Dados de horários atualizados em', 'Atualizados em')}
                </span>
                {validity !== null && (
                  <p className="board__detail">Os horários carregados são {validity}.</p>
                )}
              </li>
            )}
          </ul>
        </section>

        <div className="status__notes">
          <section>
            <h2 className="status__subtitle">O que é verificado</h2>
            <p>
              Ao abrir esta página, a aplicação contacta o serviço de dados, a base de dados e o
              motor de percursos. Não há recolha de dados de utilização.
            </p>
          </section>
          <section>
            <h2 className="status__subtitle">Sobre os horários</h2>
            <p>
              São os horários programados e publicados em dados abertos, sem informação em tempo
              real. Projeto da JSD Lousada, sem fins lucrativos. Não é um serviço da Câmara
              Municipal de Lousada.
            </p>
          </section>
        </div>
      </main>

      <footer className="page-footer">
        <SiteFooter />
      </footer>
    </div>
  );
}
